import React, { useEffect, useState } from "react";
import { Text, Flex, Card, Button } from "@tremor/react";
import {
  PlusCircleIcon,
  AdjustmentsVerticalIcon,
} from "@heroicons/react/24/outline";
import { toFriendlyDate, toFriendlyTime } from "../../services/utils";
import * as deviceAPI from "../../services/device";
import Chart from "./Chart";
import Value from "./Value";
import Gauge from "./Gauge";
import Actuator from "./Actuator";
import Map from "./Map";

const ANALOG_ACTUATOR = "analog_actuator";
const DIGITAL_ACTUATOR = "digital_actuator";

const chartDateFilters = [
  { label: "D", value: "1d", alt: "Last 24 Hours" },
  { label: "W", value: "1w", alt: "Last 7 Days" },
  { label: "M", value: "1m", alt: "Last 30 Days" },
  { label: "6M", value: "6m", alt: "Last 6 Months" },
  { label: "YTD", value: "ytd", alt: "Year to Date" },
];

export default function Widget({
  deviceId,
  capability,
  onAddCapability,
  onEditCapability,
  onActuatorAction,
}) {
  const [data, setData] = useState([]);
  const [isActuator, setIsActuator] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // New state to track initialization
  const [widgetType, setWidgetType] = useState("metric");
  const [selectedFilter, setSelectedFilter] = useState("1d");

  function transformData(data, dateFilter = '1d') {
    // format the data to be used by the chart
    if (data.length === 0) return [];
    
    // Use different date formatting based on the selected filter
    const formatDate = (timestamp) => {
      const date = new Date(timestamp);
      switch (dateFilter) {
        case '1d':
          // For daily view, show time
          return toFriendlyTime(timestamp);
        case '1w':
          // For weekly view, show day and time
          return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        case '1m':
          // For monthly view, show date
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        case '6m':
        case 'ytd':
          // For longer views, show month and day
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        default:
          return toFriendlyDate(timestamp);
      }
    };
    
    return data.map((item) => ({
      date: formatDate(item.timestamp),
      Measurement: item.value,
    }));
  }

  // Effect to highlight the metric value when it changes
  useEffect(() => {
    if (!isInitialized) return; // Skip the first render
    setHighlight(true);
    const timeout = setTimeout(() => setHighlight(false), 500); // Reset after 500ms

    if (data.length !== 0) {
      // lookup the latest value and timestamp in the data array
      const latestValue = data[data.length - 1];

      if (latestValue.Measurement !== capability.value) {
        const newMetric = {
          date: toFriendlyTime(Date.now()),
          Measurement: capability.value,
        };
        setData([...data, newMetric]);
      }
    }

    return () => clearTimeout(timeout);
  }, [capability.value, isInitialized]);

  useEffect(() => {
    const fetchData = async () => {
      // todo: filter by last 24 hours only
      if (!capability.channel) return;
      const result = await deviceAPI.fetchMetric(
        deviceId,
        capability.channel,
        "",
      );
      const chartData = transformData(result, selectedFilter);
      if (capability.widget && capability.widget.type) {
        setWidgetType(capability.widget.type);
      } else if (capability.type === "digital_actuator") {
        setWidgetType("switch");
      } else if (capability.type === ANALOG_ACTUATOR) {
        setWidgetType("slider");
      } else if (capability.type === "gps") {
        setWidgetType("map");
      } else {
        setWidgetType("metric");
      }

      setData(chartData);
      setIsInitialized(true);
    };

    if (
      capability.type === "digital_actuator" ||
      capability.type === ANALOG_ACTUATOR
    ) {
      setIsActuator(true);
    }

    if (capability.type === "gps") {
      setWidgetType("map");
    }

    if (!capability.new || !capability._id) {
      fetchData();
    }
  }, [
    capability._id,
    capability.channel,
    capability.new,
    capability.type,
    deviceId,
    capability.widget,
  ]);

  const handleToggle = (value) => {
    if (capability.type === "digital_actuator") {
      if (value) {
        capability.value = 1;
      } else {
        capability.value = 0;
      }
    } else if (capability.type === ANALOG_ACTUATOR) {
      capability.value = value;
    }

    onActuatorAction(capability);
  };

  const onDateFilter = async (e) => {
    setSelectedFilter(e.value);
    const options = "date_filter=" + e.value;
    const result = await deviceAPI.fetchMetric(
      deviceId,
      capability.channel,
      options,
    );
    const chartData = transformData(result, e.value);
    setData(chartData);
  };

  return (
    <Card
      decoration="top"
      decorationColor={capability.new ? "green" : "indigo"}
      className="h-full"
    >
      <Flex alignItems="start" className="gap-2 group">
        <Text className="widget-name z-widget-handle hover:cursor-pointer hover:cursor-move flex-1 text-sm sm:text-base">
          {capability.name || capability.type}
        </Text>
        {!capability.new && (
          <button
            onClick={() => onEditCapability(capability)}
            className="inline-flex items-center gap-1 rounded p-1 sm:p-2 text-tremor-content dark:text-dark-tremor-content hover:text-tremor-content-emphasis dark:hover:text-dark-tremor-content-emphasis hover:bg-tremor-background-subtle dark:hover:bg-dark-tremor-background-subtle"
          >
            <AdjustmentsVerticalIcon className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="hidden group-hover:inline text-xs">Edit</span>
          </button>
        )}
        {capability.new && (
          <Button
            variant="light"
            onClick={() => onAddCapability(capability)}
            size="xs"
            className="border-1 p-1 sm:p-2"
            icon={PlusCircleIcon}
          >
            <span className="hidden sm:inline">Add</span>
          </Button>
        )}
      </Flex>
      {widgetType === "areachart" || widgetType === "barchart" ? (
        <div className="mt-2">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5 inline-flex gap-0.5">
            {chartDateFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => onDateFilter(filter)}
                title={filter.alt}
                className={`rounded-md px-3 py-1 text-sm transition-colors ${
                  selectedFilter === filter.value
                    ? 'bg-white dark:bg-gray-900 shadow-sm font-medium text-gray-900 dark:text-gray-50'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 cursor-pointer'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        ""
      )}
      {widgetType === "metric" ? (
        <Value capability={capability} />
      ) : widgetType === "areachart" || widgetType === "barchart" ? (
        <Chart data={data} capability={capability} />
      ) : widgetType === "gauge" ? (
        <Gauge capability={capability} />
      ) : widgetType === "switch" || widgetType === "slider" ? (
        <Actuator capability={capability} handleToggle={handleToggle} />
      ) : widgetType === "map" ? (
        <Map capability={capability} />
      ) : (
        <Text>Unsupported widget type</Text>
      )}
      <div className="mt-5 mb-5 flex justify-left">
        {/* <Text>Last 24 hours</Text> */}
      </div>
    </Card>
  );
}
